import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, defineAsyncComponent, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import DropdownFilters from '../components/DropdownFilters.vue'
import { useStationStore } from '@/pinia-store/useStationStore'
import { useSectorStore } from '@/pinia-store/useSectorStore'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { catalogDisplayName } from '@/utils/stationDisplay'


export default /*#__PURE__*/_defineComponent({
  __name: 'SectorFilter',
  setup(__props) {

const sectorIcon = defineAsyncComponent(() => import('@/assets/icons/sector.vue'))
const stationStore = useStationStore()
const sectorStore = useSectorStore()
const { listStationsQuery, listStations } = storeToRefs(stationStore)
const { healthUnitsMap } = storeToRefs(useHealthUnitStore())

/**
 * Unidades já vistas na grade (nome por id). Acumula entre cargas: filtrar por um setor
 * sem plantões esvazia a grade, e a opção selecionada precisa continuar no dropdown
 * para poder ser desmarcada.
 */
const seenUnits = ref(new Map<number, string>())
watch(
  listStations,
  (tree) => {
    const next = new Map(seenUnits.value)
    tree?.data?.forEach((company) =>
      company.healthUnities.forEach((unit) => next.set(unit.healthUnitId, unit.healthUnitName))
    )
    if (next.size !== seenUnits.value.size) seenUnits.value = next
  },
  { immediate: true }
)

/** Unidades do filtro "Unidade" (escolha do usuário: conjunto pequeno). */
const filterUnitIds = computed<number[]>(() =>
  (listStationsQuery.value.healthUnits ?? []).map(Number).filter(Boolean)
)

/** Setor é do catálogo da unidade: usa as unidades filtradas ou, sem filtro, as que já apareceram na tela. */
const unitIds = computed<number[]>(() =>
  filterUnitIds.value.length ? filterUnitIds.value : [...seenUnits.value.keys()]
)

/**
 * Cada `GET /sectors/healthUnit/:id` passa por checagem no SpiceDB: nada de buscar o catálogo de
 * todas as unidades da grade ao montar. Busca ao abrir o dropdown (`open:popper` do Popper, que
 * chega por fallthrough do DropdownFilters). Antes de abrir, só as unidades do filtro quando há
 * setor selecionado, para os chips resolverem o nome; sem filtro de unidade o chip fica em `#id`.
 */
const opened = ref(false)
const unitsToFetch = computed<number[]>(() => {
  if (opened.value) return unitIds.value
  return (listStationsQuery.value.sectorIds ?? []).length ? filterUnitIds.value : []
})

watch(unitsToFetch, (ids) => ids.forEach((id) => void sectorStore.fetchByHealthUnit(id)), {
  immediate: true
})

const unitName = (unitId: number): string | undefined =>
  healthUnitsMap.value.get(unitId) ?? seenUnits.value.get(unitId)

const items = computed(() => [
  { title: 'Sem setor', value: 'none' },
  ...unitIds.value.flatMap((unitId) =>
    sectorStore.sectorsOf(unitId).map((sector) => ({
      title: catalogDisplayName(sector),
      value: String(sector.id),
      subtitle: unitName(unitId)
    }))
  )
])

const selected = computed(() => [
  ...(listStationsQuery.value.sectorIds ?? []),
  ...(listStationsQuery.value.withoutSector ? ['none'] : [])
])

async function toggle(item: { value: string }): Promise<void> {
  const current = new Set(listStationsQuery.value.sectorIds ?? [])
  let withoutSector = !!listStationsQuery.value.withoutSector
  if (item.value === 'none') withoutSector = !withoutSector
  else if (current.has(item.value)) current.delete(item.value)
  else current.add(item.value)
  await stationStore.setSectorFilter({ sectorIds: [...current], withoutSector })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DropdownFilters, {
    title: "Setor",
    badge: "NOVO",
    items: items.value,
    icon: _unref(sectorIcon),
    multiple: "",
    "selected-value": selected.value,
    onClicked: toggle,
    "onOpen:popper": _cache[0] || (_cache[0] = ($event: any) => (opened.value = true))
  }, null, 8, ["items", "icon", "selected-value"]))
}
}

})